import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, Mail, Save, RotateCcw, Loader2, Zap, Eye } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

/* ==================== Admin: Message Templates ==================== */
/* Every automated SMS / email the system sends, with its trigger, editable
   wording, [Variable] chips, live preview, Save and Reset-to-default.
   Trigger logic is untouched — only the copy is editable.                 */
export const MessageTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("sms");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/message-templates`);
      setTemplates(data.templates || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't load templates"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => templates.filter((t) => t.channel === tab), [templates, tab]);
  const counts = useMemo(() => ({
    sms: templates.filter((t) => t.channel === "sms").length,
    email: templates.filter((t) => t.channel === "email").length,
  }), [templates]);

  const patch = (key, updated) => setTemplates((prev) => prev.map((t) => (t.key === key ? { ...t, ...updated } : t)));

  return (
    <div className="space-y-6" data-testid="message-templates-page">
      <div>
        <h1 className="font-serif text-3xl text-[#FFD166]">Message Templates</h1>
        <p className="text-sm text-[#B9C8D5] mt-1 max-w-2xl">
          Every automated text and email the system sends. Edit the wording, keep the <span className="text-[#FFD166]">[Variables]</span> where you
          want live details filled in, and hit Save — changes apply to the very next message. Triggers never change from here.
        </p>
      </div>

      <div className="flex gap-2" data-testid="mt-tabs">
        {[["sms", "Text messages", MessageSquareText], ["email", "Emails", Mail]].map(([k, label, Icon]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            data-testid={`mt-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 border transition-colors ${
              tab === k ? "bg-[#FFD166] text-[#0A1A26] border-[#FFD166]" : "border-[#FFD166]/30 text-[#B9C8D5] hover:bg-[#FFD166]/10"
            }`}
          >
            <Icon className="w-4 h-4" /> {label} <span className="text-xs opacity-70">({counts[k]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#B9C8D5]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2">
          {shown.map((t) => <TemplateCard key={t.key} template={t} onSaved={(u) => patch(t.key, u)} />)}
        </div>
      )}
    </div>
  );
};

const applySample = (text, variables) =>
  variables.reduce((acc, v) => acc.split(v.token).join(v.sample), text || "");

const TemplateCard = ({ template: t, onSaved }) => {
  const [fields, setFields] = useState(t.current);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const refs = useRef({});
  const [focused, setFocused] = useState(t.fields[t.fields.length - 1].name);
  const current = t.current;
  useEffect(() => { setFields(current); }, [current]);

  const dirty = t.fields.some((f) => (fields[f.name] || "") !== (t.current[f.name] || ""));
  const isDefault = t.fields.every((f) => (fields[f.name] || "") === (t.defaults[f.name] || ""));

  const insertToken = (token) => {
    const name = focused;
    const el = refs.current[name];
    const val = fields[name] || "";
    const start = el?.selectionStart ?? val.length;
    const end = el?.selectionEnd ?? val.length;
    const next = val.slice(0, start) + token + val.slice(end);
    setFields((p) => ({ ...p, [name]: next }));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/message-templates/${t.key}`, { fields });
      onSaved({ current: data.fields, customized: true, updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success(`${t.label} saved — live for the next message ✓`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Restore the default wording for "${t.label}"?`)) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/message-templates/${t.key}/reset`);
      onSaved({ current: data.defaults, customized: false, updated_at: null, updated_by: null });
      toast.success("Default wording restored");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setSaving(false); }
  };

  const smsLen = t.channel === "sms" ? applySample(fields.body, t.variables).length : 0;

  return (
    <Card className="p-5 flex flex-col gap-4" data-testid={`mt-card-${t.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif text-lg text-[#F6F1E4]" data-testid={`mt-label-${t.key}`}>{t.label}</h3>
            {t.customized && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFD166]/15 text-[#FFD166] border border-[#FFD166]/30" data-testid={`mt-customized-${t.key}`}>Customized</span>}
          </div>
          <div className="mt-1.5 flex items-start gap-1.5 text-xs text-[#B9C8D5]" data-testid={`mt-trigger-${t.key}`}>
            <Zap className="w-3.5 h-3.5 text-[#F4A93A] mt-0.5 shrink-0" />
            <span>{t.trigger}</span>
          </div>
        </div>
        <button type="button" onClick={() => setPreview((p) => !p)} className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full border ${preview ? "border-[#FFD166] text-[#FFD166]" : "border-[#FFD166]/30 text-[#B9C8D5] hover:text-[#FFD166]"}`} data-testid={`mt-preview-toggle-${t.key}`}>
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
      </div>

      {t.variables.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`mt-variables-${t.key}`}>
          {t.variables.map((v) => (
            <button
              key={v.token}
              type="button"
              title={`${v.desc} — click to insert`}
              onClick={() => insertToken(v.token)}
              className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-black/30 border border-[#FFD166]/25 text-[#FFD166] hover:bg-[#FFD166]/15"
              data-testid={`mt-var-${t.key}-${v.token.replace(/[^a-z]/gi, "").toLowerCase()}`}
            >
              {v.token}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {t.fields.map((f) => (
          <div key={f.name}>
            <div className="flex justify-between items-baseline">
              <label className="text-xs uppercase tracking-[0.2em] text-[#B9C8D5]">{f.label}</label>
              {f.name === "body" && t.channel === "sms" && (
                <span className={`text-[10px] ${smsLen > 320 ? "text-red-400" : smsLen > 160 ? "text-[#F4A93A]" : "text-[#B9C8D5]"}`} data-testid={`mt-sms-len-${t.key}`}>
                  ~{smsLen} chars · {Math.max(1, Math.ceil(smsLen / (smsLen > 160 ? 153 : 160)))} segment{smsLen > 160 ? "s" : ""}
                </span>
              )}
            </div>
            {preview ? (
              <div className="mt-1 whitespace-pre-wrap text-sm text-[#F6F1E4] bg-[#FFD166]/5 border border-dashed border-[#FFD166]/30 rounded-lg px-3 py-2" data-testid={`mt-preview-${t.key}-${f.name}`}>
                {applySample(fields[f.name], t.variables)}
              </div>
            ) : f.multiline ? (
              <textarea
                ref={(el) => { refs.current[f.name] = el; }}
                value={fields[f.name] || ""}
                onChange={(e) => setFields((p) => ({ ...p, [f.name]: e.target.value }))}
                onFocus={() => setFocused(f.name)}
                rows={Math.min(8, Math.max(3, Math.ceil((fields[f.name] || "").length / 70)))}
                className="mt-1 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#FFD166] resize-y"
                data-testid={`mt-field-${t.key}-${f.name}`}
              />
            ) : (
              <input
                ref={(el) => { refs.current[f.name] = el; }}
                value={fields[f.name] || ""}
                onChange={(e) => setFields((p) => ({ ...p, [f.name]: e.target.value }))}
                onFocus={() => setFocused(f.name)}
                className="mt-1 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#FFD166]"
                data-testid={`mt-field-${t.key}-${f.name}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 pt-1 border-t border-[#FFD166]/10">
        <span className="text-[10px] text-[#B9C8D5]/70" data-testid={`mt-updated-${t.key}`}>
          {t.updated_at ? `Last saved ${new Date(t.updated_at).toLocaleString()}${t.updated_by ? ` · ${t.updated_by}` : ""}` : "Using default wording"}
        </span>
        <div className="flex gap-2">
          {!isDefault && (
            <Btn variant="ghost" onClick={reset} disabled={saving} data-testid={`mt-reset-${t.key}`}>
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </Btn>
          )}
          <Btn onClick={save} disabled={saving || !dirty} className={!dirty ? "opacity-50" : ""} data-testid={`mt-save-${t.key}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </Btn>
        </div>
      </div>
    </Card>
  );
};

export default MessageTemplates;
